import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react';
import './SampleTree.less';
import { Tree } from '../Tree/Legacy/Tree';
import {
  scrollToElement,
  UPDATE_EVENT_PREFIX,
  TreeCommandType,
  useTreeModel,
  deleteNode,
} from '../Tree/Legacy/useTreeModel';
import { TreeNodeContent } from '../Tree/Legacy/TreeNodeContent';
import { ITreeNode } from '../Tree/Legacy/TreeNode';
import { MappingContext } from '@decisions/server/DecisionsFramework/Design/Flow/Service/MappingContext';
import { HighlightMatch } from '../lists/HighlightMatch';
import {
  SampleCategory,
  SampleForTree,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/Samples';
import { InlineActions } from './InlineActions';
import { ISamplesApiFactory, SamplesApiFactory } from './SamplesApiFactory';
import classNames from 'classnames';
import { UnitTest } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Debugging/UnitTest';
import { generateGuid } from 'utils/Guid';
import { isNullOrWhitespace } from 'utils/StringUtils';
import { Loading } from 'components/Loading';
import { DebounceComponent } from 'components/DebounceComponent';

export const DESELECTALL_EVENT_PREFIX: string = `tree-deselect-all__`;
export const DELETENODE_EVENT_PREFIX: string = `tree-delete-node__`;
const LOADMORE_NODE_ID: string = `LoadMore`;

export interface ISampleTreeProps {
  treeId?: string;
  mappingContext: MappingContext;
  selectedNodeId?: string;
  defaultId?: string;
  searchCriteria?: string;
  onAddNewClick: () => void;
  onSampleSelected?: (sample: SampleForTree) => void;
  onCategorySelected?: (sampleCategory: SampleCategory) => void;
  sampleActions?: InlineAction[];
  categoryActions?: InlineAction[];
  apiFactory?: ISamplesApiFactory;
  className?: string;
}

export interface SampleTreeType {
  nodeName: string;
  nodePath: string;
  hasChildren?: boolean;
  entity?: SampleCategory | SampleForTree;
  parentId?: string;
  pageIndex?: number;
  nextPage?: ITreeNode<SampleTreeType>[];
}

export interface InlineAction {
  action: (id: string, el?: HTMLElement) => void;
  className: string;
  title?: string;
  canBeAdded?: (item: SampleCategory | SampleForTree | UnitTest) => boolean;
}

export function getNodeContentClassName(isActive: boolean, isRoot: boolean = false) {
  return classNames('dp-sample-tree__node-content', {
    'dp-sample-tree__node-content--selected': isActive,
    'dp-sample-tree__node-content--root': isRoot,
  });
}

export function SampleTree({
  treeId,
  mappingContext,
  selectedNodeId,
  defaultId,
  searchCriteria,
  onAddNewClick,
  onSampleSelected,
  onCategorySelected,
  sampleActions,
  categoryActions,
  apiFactory,
  className,
}: ISampleTreeProps) {
  const [id] = useState<string>(treeId || generateGuid());
  const [selectedId, setSelectedId] = useState(selectedNodeId || '');
  const [isLoading, setIsLoading] = useState(false);
  const scrollToSelectedNode = useRef(true);

  const loadSampleNodesPage = useCallback(
    async (parent: ITreeNode<SampleTreeType>, pageIndex: number): Promise<ITreeNode<SampleTreeType>[]> => {
      const pageSamples: SampleForTree[] = await (apiFactory || SamplesApiFactory(mappingContext))!.getByCategory(
        parent.nodeId,
        parent.data?.entity ? (parent.data.entity as SampleCategory).LimitNodesCount : undefined,
        pageIndex
      );

      return (pageSamples ?? []).map((sample: SampleForTree) => {
        const path =
          parent.data.nodePath && parent.data.nodePath.length ? parent.data.nodePath + '.' + sample.Id : sample.Id;
        return convertSampleToNode(sample, path, selectedId);
      });
    },
    [mappingContext, selectedId, apiFactory]
  );

  const loadSampleNodes = useCallback(
    async (parent: ITreeNode<SampleTreeType>): Promise<ITreeNode<SampleTreeType>[]> => {
      const sampleNodes = await loadSampleNodesPage(parent, 0);

      if (parent.data?.entity && (parent.data.entity as SampleCategory).LimitNodesCount > 0) {
        const nextPage = await loadSampleNodesPage(parent, 1);
        if (nextPage.length) {
          sampleNodes.push(createLoadMoreNode(parent, 1, nextPage));
        }
      }
      return sampleNodes;
    },
    [loadSampleNodesPage]
  );

  const { model, setModel, expandNode, toggleSelectNode, unselectNodes, findNode, getActiveNodes } = useTreeModel(
    [],
    loadSampleNodes
  );

  const setSelection = useCallback(
    (id: string) => {
      setSelectedId(id);

      unselectNodes();
      if (!isNullOrWhitespace(id)) {
        toggleSelectNode(id, true);
      }
    },
    [toggleSelectNode, unselectNodes]
  );

  useEffect(() => {
    setSelection(selectedNodeId || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNodeId]);

  const loadTree = useCallback(async () => {
    setIsLoading(true);
    let rootNode: ITreeNode<SampleTreeType>[] = await getRootNodes(
      searchCriteria,
      apiFactory,
      mappingContext,
      selectedId
    );

    for (const node of rootNode[0].children ?? []) {
      if (node.isExpanded) {
        // eslint-disable-next-line no-await-in-loop
        node.children = await loadSampleNodes(node);

        node.children
          .filter(sampleNode => sampleNode.isActive)
          .forEach(sampleNode => {
            if (sampleNode.data?.entity && onSampleSelected) {
              onSampleSelected(sampleNode.data.entity as SampleForTree);
            }
          });
      }
    }
    setIsLoading(false);
    setModel([...rootNode]);
  }, [searchCriteria, apiFactory, mappingContext, selectedId, setModel, loadSampleNodes, onSampleSelected]);

  useEffect(() => {
    if (model.length === 0) {
      loadTree();
    }
  }, [model, loadTree]);

  const onNodeSelected = useCallback(
    async (node: ITreeNode<SampleTreeType>) => {
      if (node.nodeId === LOADMORE_NODE_ID) {
        const parent = model[0].children?.filter(c => c.nodeId === node.data?.parentId)[0];

        const moreNode = parent?.children?.pop();
        if (moreNode?.data?.nextPage && parent?.children) {
          parent.children = parent.children.concat(moreNode.data.nextPage);

          const newPageIndex: number = (moreNode?.data?.pageIndex ?? 0) + 1;
          if (parent?.data?.entity && (parent.data.entity as SampleCategory).LimitNodesCount > 0) {
            const nextPage = await loadSampleNodesPage(parent, newPageIndex);
            if (nextPage.length) {
              parent.children.push(createLoadMoreNode(parent, newPageIndex + 1, nextPage));
            }
          }
        }

        setModel([...model]);
      } else if (node.data?.entity && isSample(node.data.entity) && onSampleSelected) {
        onSampleSelected(node.data.entity as SampleForTree);
        unselectNodes();
        toggleSelectNode(node.nodeId, true);
      } else if (node.data?.entity && (node.data.entity as SampleCategory).CanBeExecuted && onCategorySelected) {
        onCategorySelected(node.data.entity as SampleCategory);
        unselectNodes();
        toggleSelectNode(node.nodeId, true);
      }
    },
    [onSampleSelected, onCategorySelected, toggleSelectNode, unselectNodes, model, setModel, loadSampleNodesPage]
  );

  const getNode = useCallback(
    (node: ITreeNode<SampleTreeType>) => {
      return (
        <TreeNodeContent
          node={node}
          className={getNodeContentClassName(!!node.isActive, !node.data?.entity)}
          onExpandNodeClick={expandNode}
          hasChildren={node.data?.hasChildren && (node.nodeId !== 'root' || !!node.children?.length)}
        >
          <div
            title={`${node.data?.nodeName ?? ''}${node.nodeId === defaultId ? ' (default)' : ''}`}
            className="dpc-tree-node__name"
            onClick={() => onNodeSelected(node)}
          >
            <HighlightMatch searchString={node.nodeId === 'root' ? undefined : searchCriteria ?? undefined}>
              {node.data?.nodeName ?? ''}
            </HighlightMatch>
            {node.nodeId === defaultId ? ' (default)' : ''}
          </div>
          <InlineActions
            node={node}
            onAddNewClick={onAddNewClick}
            actions={
              node.data?.entity && isSample(node.data.entity)
                ? sampleActions
                : node.data?.entity
                ? categoryActions
                : undefined
            }
          />
        </TreeNodeContent>
      );
    },
    [searchCriteria, expandNode, defaultId, onAddNewClick, sampleActions, categoryActions, onNodeSelected]
  );

  const refreshNode = useCallback(
    async (id: string) => {
      let sampleNode = findNode(id);
      let sample = await (apiFactory || SamplesApiFactory(mappingContext))!.getSample(id);
      if (!sample) {
        return;
      }

      if (sampleNode) {
        getActiveNodes().forEach(node => {
          node.isActive = false;
        });

        sampleNode.data.nodeName = sample.Name;
        sampleNode.isActive = true;
      } else {
        let category = findNode(sample.Category.Id);

        if (!category) {
          category = convertCategoryToNode(sample.Category, 'root');
          model[0].children?.push(category);
        }
        category.children = await loadSampleNodes(category);
        getActiveNodes().forEach(node => {
          node.isActive = false;
        });
        var selectedNode = category.children.find(node => node.nodeId === id);
        if (selectedNode) selectedNode.isActive = true;
        category.isExpanded = true;
      }
      scrollToSelectedNode.current = true;
      setSelectedId(id);
      setModel([...model]);
    },
    [findNode, apiFactory, mappingContext, setModel, model, getActiveNodes, loadSampleNodes]
  );

  const deleteTreeNode = useCallback(
    async (id: string) => {
      const sampleNode = findNode(id);
      const category = findNode(sampleNode?.data?.parentId!);

      if (category?.children?.length === 1) deleteNode(category.nodeId, model);
      else deleteNode(id, model);

      getActiveNodes().forEach(node => {
        node.isActive = false;
      });
      setSelectedId('');
      setModel([...model]);
    },
    [findNode, getActiveNodes, model, setModel]
  );

  const handleUpdateEvent = useCallback(
    async (e: CustomEvent<{ id: string; command: TreeCommandType }>) => {
      const command = e?.detail?.command;
      const id = e?.detail?.id;

      if (command !== undefined && !isNullOrWhitespace(id)) {
        switch (command) {
          case TreeCommandType.Add:
          case TreeCommandType.Refresh:
            refreshNode(id);
            break;
          case TreeCommandType.Delete:
            deleteTreeNode(id);
            break;
          default:
            break;
        }
      }
    },
    [deleteTreeNode, refreshNode]
  );

  useLayoutEffect(() => {
    //scroll to node
    if (scrollToSelectedNode.current) {
      const selectedElements = document
        .getElementById(id)
        ?.getElementsByClassName('dp-sample-tree__node-content--selected');
      const selectedElem = selectedElements?.item(selectedElements?.length - 1);
      if (selectedElem) {
        scrollToSelectedNode.current = false;
        scrollToElement(selectedElem);
      }
    }

    window.addEventListener(`${DESELECTALL_EVENT_PREFIX}${id}`, unselectNodes);
    window.addEventListener(`${UPDATE_EVENT_PREFIX}${id}`, handleUpdateEvent as any);

    return () => {
      window.removeEventListener(`${DESELECTALL_EVENT_PREFIX}${id}`, unselectNodes);
      window.removeEventListener(`${UPDATE_EVENT_PREFIX}${id}`, handleUpdateEvent as any);
    };
  }, [unselectNodes, id, handleUpdateEvent]);

  return (
    <>
      {isLoading && (
        <DebounceComponent>
          <Loading className="full-size" />
        </DebounceComponent>
      )}
      <Tree
        treeId={id}
        treeClassName={classNames('dp-sample-tree', className)}
        nodeClassName="dp-sample-tree__node"
        nodes={model}
        getTreeNode={getNode}
      />
    </>
  );
}

async function getRootNodes(
  searchCriteria: string | undefined,
  apiFactory: ISamplesApiFactory | undefined,
  mappingContext: MappingContext,
  selectId: string | undefined
) {
  let rootNode: ITreeNode<SampleTreeType>[] = [];

  if (searchCriteria) {
    let data = await (apiFactory || SamplesApiFactory(mappingContext))!.search(searchCriteria);
    data = data || [];

    rootNode = [
      {
        nodeId: 'root',
        data: { nodeName: 'Sample Data', hasChildren: true },
        isActive: false,
        isExpanded: true,
        children: data.sort(sortSamples).map((sample: SampleForTree) => {
          return convertSampleToNode(sample, sample.Id);
        }),
      },
    ] as ITreeNode<SampleTreeType>[];
  } else {
    let openCategoryId: string;
    if (selectId) {
      const openCategory = await (apiFactory || SamplesApiFactory(mappingContext))!.getCategory(selectId);
      if (openCategory) {
        openCategoryId = openCategory.Id;
      }
    }

    const categories = await (apiFactory || SamplesApiFactory(mappingContext))!.getCategories();

    rootNode = [
      {
        nodeId: 'root',
        data: { nodeName: 'Sample Data', hasChildren: true },
        isActive: false,
        isExpanded: true,
        children: (categories ?? []).map(category => convertCategoryToNode(category, 'root', openCategoryId, selectId)),
      },
    ] as ITreeNode<SampleTreeType>[];
  }
  return rootNode;
}

function convertCategoryToNode(
  category: SampleCategory,
  nodePath: string,
  openCategoryId?: string,
  selectId?: string
): ITreeNode<SampleTreeType> {
  const path = nodePath && nodePath.length ? nodePath + '.' + category.Id : category.Id;

  return {
    nodeId: category.Id,
    data: { nodeName: category.Title, nodePath: path, hasChildren: true, entity: category },
    isActive: selectId === category.Id,
    isExpanded: openCategoryId === category.Id,
  } as ITreeNode<SampleTreeType>;
}

function convertSampleToNode(sample: SampleForTree, path: string, selectId?: string) {
  return {
    nodeId: sample.Id,
    data: {
      nodeName: sample.Name,
      nodePath: path,
      hasChildren: false,
      entity: sample,
      parentId: sample.Category && sample.Category.Id,
    },
    isActive: selectId === sample.Id,
    isExpanded: false,
  } as ITreeNode<SampleTreeType>;
}

function sortSamples(a: SampleForTree, b: SampleForTree): number {
  const at = a.Name || a.Id;
  const bt = b.Name || b.Id;

  const an = parseInt(at);
  const bn = parseInt(bt);
  if (!isNaN(an) && !isNaN(bn)) {
    return an - bn;
  } else {
    return at.localeCompare(bt);
  }
}

function isSample(obj: SampleCategory | SampleForTree | UnitTest): obj is SampleCategory | SampleForTree {
  return 'Category' in obj;
}

function createLoadMoreNode(
  parent: ITreeNode<SampleTreeType>,
  pageIndex: number,
  nextPage: ITreeNode<SampleTreeType>[]
): ITreeNode<SampleTreeType> {
  return {
    nodeId: LOADMORE_NODE_ID,
    data: {
      pageIndex: pageIndex,
      nextPage: nextPage,
      nodeName: 'More...',
      hasChildren: false,
      parentId: parent.nodeId,
    },
    isActive: false,
    isExpanded: false,
  } as ITreeNode<SampleTreeType>;
}
